import type {
  DiagramNodeData,
  DiagramLinkData,
  ItemInfo,
  Bounds,
  Position,
  Dimensions,
  DatatipContext,
  DiagramLabelStyles,
  DiagramLabelPosition,
  DiagramLayoutOutput,
  PromotedLinkId
} from '../diagram.types';
import type { RefObject } from 'preact';
import type { BundleType } from '#resources/nls/bundle';
import type { Font, GetTextDimensionsType, TextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { generateAriaLabel } from '#utils/UNSAFE_visUtils/accUtils';
import { DatatipConfig } from '#utils/UNSAFE_visTypes/common';
import { addKey, containsKey, Keys, removeKey } from '#utils/UNSAFE_keys';

/**
 * Constructs the json object to be passed into the layout function
 */
export function constructLayoutJSON<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  nodeItems: Map<K1, D1>,
  linkItems: Map<K2, D2>,
  promotedLinks: PromotedLinkId<K1, D2>[],
  dimensions: Map<K1, Dimensions>,
  width: number,
  height: number,
  zoom: number,
  nodeIdToParentMap: Map<K1, K1>,
  rootRef?: RefObject<HTMLDivElement>,
  getTextDimensions?: GetTextDimensionsType
) {
  const labelDims: Map<K1 | K2, TextDimensions> = new Map();
  const nodeValues = Array.from(nodeItems.values());
  const linkValues = Array.from(linkItems.values());
  if (getTextDimensions) {
    getLabelProps(nodeValues, linkValues, labelDims, getTextDimensions);
  }

  const linkKeys = Array.from(linkItems.keys());
  for (const id of linkKeys) {
    const item = linkItems.get(id as K2);
    if (item && getTextDimensions && item.label) {
      const label = item.label;
      const fontProps = _getFontProps(item.labelStyle);
      labelDims.set(id as K1 | K2, getTextDimensions(label, fontProps));
    }
  }
  const getLabelDimensions = function (data: D1 | D2) {
    return labelDims.get(data.id);
  };
  const getNodeDimensions = function (data: D1) {
    return dimensions.get(data.id);
  };
  const getRelativeNodeBounds = function (id: K1, containerId?: K1) {
    const absPos = getAbsoluteNodePosition(id, containerId, rootRef);
    if (absPos) {
      return {
        x: absPos.x / zoom,
        y: absPos.y / zoom,
        width: absPos.width / zoom,
        height: absPos.height / zoom
      };
    } else {
      return undefined;
    }
  };

  const getContainerId = function (startNode: K1, endNode: K1) {
    return getCommonAncestorId(startNode, endNode, nodeIdToParentMap);
  };

  const getPromotedLinks = function () {
    return promotedLinks;
  };

  return {
    layoutJSON: {
      getNodeDimensions: getNodeDimensions,
      getLabelDimensions: getLabelDimensions,
      getContainerId,
      getRelativeNodeBounds,
      getPromotedLinks,
      componentSize: {
        width: width,
        height: height
      }
    },
    labelDims: labelDims
  };
}

function normalizeBounds(bounds: Bounds, zoom: number) {
  return {
    x: bounds.x / zoom,
    y: bounds.y / zoom,
    width: bounds.width / zoom,
    height: bounds.height / zoom
  };
}

function getAbsoluteLinkBounds<K1, K2, D2>(
  itemId: K2 | PromotedLinkId<K1, D2>,
  referenceId: K1 | undefined,
  bounds: Map<K2 | PromotedLinkId<K1, D2>, Bounds | Dimensions>,
  zoom: number,
  rootRef?: RefObject<HTMLDivElement>,
  isPromoted?: boolean
) {
  if (rootRef && rootRef.current) {
    let linkElement;
    if (isPromoted) {
      const id = `${(itemId as PromotedLinkId<K1, D2>).startNode}_${
        (itemId as PromotedLinkId<K1, D2>).endNode
      }`;
      linkElement = rootRef.current.querySelector(`[data-oj-private-promoted-link=${id}]`);
    } else {
      linkElement = rootRef.current.querySelector(`[data-oj-private-link=${itemId}]`);
    }
    let nodeContainer;
    if (referenceId) {
      nodeContainer = rootRef.current.querySelector(`[data-oj-private-node=${referenceId}]`);
    }
    const dims = bounds.get(itemId);
    if (linkElement && nodeContainer && dims) {
      const linkBoundingRect = normalizeBounds(linkElement.getBoundingClientRect(), zoom);
      const diagramBoundingRect = normalizeBounds(nodeContainer.getBoundingClientRect(), zoom);
      return {
        x: linkBoundingRect.x - diagramBoundingRect.x,
        y: linkBoundingRect.y - diagramBoundingRect.y,
        width: dims.width,
        height: dims.height
      };
    }
  }
  return null;
}

export function getBounds<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  nodes: D1[],
  links: D2[],
  promotedLinks: PromotedLinkId<K1, D2>[],
  nodeBounds: Map<K1, Bounds>,
  linkDimensions: Map<K2, Dimensions>,
  promotedLinkDimensions: Map<string, Dimensions>,
  zoom: number,
  rootRef?: RefObject<HTMLDivElement>
) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let id;
  let topId;
  let leftId;
  for (let i = 0; i < nodes.length; i++) {
    id = nodes[i].id;
    const bounds = nodeBounds.get(id);
    if (bounds) {
      if (minX > bounds.x) {
        minX = bounds.x;
        leftId = id;
      }
      if (minY > bounds.y) {
        minY = bounds.y;
        topId = id;
      }
      if (maxX < bounds.x + bounds.width) {
        maxX = bounds.x + bounds.width;
      }
      if (maxY < bounds.y + bounds.height) {
        maxY = bounds.y + bounds.height;
      }
    }
  }
  const nodeMinX = minX;
  const nodeMinY = minY;
  let linkMinX = 0;
  let linkMinY = 0;
  let linkMaxX = maxX - minX;
  let linkMaxY = maxY - minY;

  for (let i = 0; i < links.length; i++) {
    id = links[i].id;
    //Top and left bounds are relative to the current most top and left nodes
    const topBounds = getAbsoluteLinkBounds(id, topId, linkDimensions, zoom, rootRef);
    const leftBounds = getAbsoluteLinkBounds(id, leftId, linkDimensions, zoom, rootRef);
    if (leftBounds) {
      if (leftBounds.x < linkMinX) {
        linkMinX = leftBounds.x;
      }
      if (linkMaxX < leftBounds.x + leftBounds.width) {
        linkMaxX = leftBounds.x + leftBounds.width;
      }
    }
    if (topBounds) {
      if (topBounds.y < linkMinY) {
        linkMinY = topBounds.y;
      }
      if (linkMaxY < topBounds.y + topBounds.height) {
        linkMaxY = topBounds.y + topBounds.height;
      }
    }
  }

  for (let i = 0; i < promotedLinks.length; i++) {
    id = promotedLinks[i];
    //Top and left bounds are relative to the current most top and left nodes
    const topBounds = getAbsoluteLinkBounds(id, topId, promotedLinkDimensions, zoom, rootRef, true);
    const leftBounds = getAbsoluteLinkBounds(
      id,
      leftId,
      promotedLinkDimensions,
      zoom,
      rootRef,
      true
    );
    if (leftBounds) {
      if (leftBounds.x < linkMinX) {
        linkMinX = leftBounds.x;
      }
      if (linkMaxX < leftBounds.x + leftBounds.width) {
        linkMaxX = leftBounds.x + leftBounds.width;
      }
    }
    if (topBounds) {
      if (topBounds.y < linkMinY) {
        linkMinY = topBounds.y;
      }
      if (linkMaxY < topBounds.y + topBounds.height) {
        linkMaxY = topBounds.y + topBounds.height;
      }
    }
  }
  return {
    minX: nodeMinX,
    minY: nodeMinY,
    contentWidth: maxX - minX,
    contentHeight: maxY - minY,
    bounds: { x: linkMinX, y: linkMinY, width: linkMaxX - linkMinX, height: linkMaxY - linkMinY }
  };
}

function getAbsoluteNodePosition<K1>(
  nodeId: K1,
  containerId?: K1,
  rootRef?: RefObject<HTMLDivElement>
) {
  if (rootRef && rootRef.current) {
    const nodeElement = rootRef.current.querySelector(`[data-oj-private-node=${nodeId}]`);
    let nodeContainer;
    if (containerId) {
      nodeContainer = rootRef.current.querySelector(`[data-oj-private-child=${containerId}]`);
    } else {
      nodeContainer = rootRef.current.querySelector('[data-oj-private-node-container]');
    }
    if (nodeElement && nodeContainer) {
      const nodeBoundingRect = nodeElement.getBoundingClientRect();
      const nodeContainerRect = nodeContainer.getBoundingClientRect();
      return {
        x: nodeBoundingRect.x - nodeContainerRect.x,
        y: nodeBoundingRect.y - nodeContainerRect.y,
        width: nodeBoundingRect.width,
        height: nodeBoundingRect.height
      };
    }
  }
  return null;
}

function getLabelProps<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramNodeData<K2, K1>
>(
  nodeItems: D1[],
  linkItems: D2[],
  labelDims: Map<K1 | K2, TextDimensions>,
  getTextDimensions: GetTextDimensionsType
) {
  nodeItems.forEach((value) => {
    if (value.nodes) {
      getNodeLabelProps<K1, K2, D1>(value.nodes, labelDims, getTextDimensions);
    }
    if (value.label) {
      const label = value.label;
      const fontProps = _getFontProps(value.labelStyle);
      labelDims.set(value.id as K1, getTextDimensions(label, fontProps));
    }
  });
  linkItems.forEach((value) => {
    if (value.label) {
      const label = value.label;
      const fontProps = _getFontProps(value.labelStyle);
      labelDims.set(value.id as K2, getTextDimensions(label, fontProps));
    }
  });
}

function getNodeLabelProps<K1, K2, D1 extends DiagramNodeData<K1, D1>>(
  nodeItems: D1[],
  labelDims: Map<K1 | K2, TextDimensions>,
  getTextDimensions: GetTextDimensionsType
) {
  nodeItems.forEach((value) => {
    if (value.nodes) {
      getNodeLabelProps<K1, K2, D1>(value.nodes, labelDims, getTextDimensions);
    }
    if (value.label) {
      const label = value.label;
      const fontProps = _getFontProps(value.labelStyle);
      labelDims.set(value.id as K1, getTextDimensions(label, fontProps));
    }
  });
}

/**
 * Deconstructs the output of the layout function
 */
export function deconstructLayoutJSON<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(layoutJSON: DiagramLayoutOutput<K1, K2>, nodeData: D1[], linkData: D2[]) {
  const positions: Map<K1, Position> = new Map();
  const nodeLabelPos: Map<K1, DiagramLabelPosition> = new Map();
  const linkLabelPos: Map<K2, DiagramLabelPosition> = new Map();
  findPoints(layoutJSON, nodeData, positions, nodeLabelPos);
  const links = layoutJSON.links;
  let link;
  if (links) {
    for (let i = 0; i < linkData.length; i++) {
      link = links[linkData[i].id];
      if (link && link.label) linkLabelPos.set(linkData[i].id, link.label);
    }
  }
  return {
    nodePoints: positions,
    nodeLabelPos: nodeLabelPos,
    linkLabelPos: linkLabelPos
  };
}

function findPoints<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1, D1>
>(
  output: DiagramLayoutOutput<K1, K2>,
  nodeData: D1[],
  positions: Map<K1, Position>,
  nodeLabelPos: Map<K1, DiagramLabelPosition>
) {
  const nodes = output.nodes;
  let node;
  if (nodes) {
    for (let i = 0; i < nodeData.length; i++) {
      const data = nodeData[i];
      const id = data.id;
      if (data.nodes) {
        findPoints(output, data.nodes, positions, nodeLabelPos);
      }
      node = nodes[id];
      if (node) {
        positions.set(id, node.position);
        if (node.label) {
          nodeLabelPos.set(id, node.label);
        }
      }
    }
  }
}

/**
 * Merges the node points into the dimensions
 */
export function mergeDimensions<K1>(
  dimensions: Map<K1, Dimensions>,
  nodePoints: Map<K1, Position>,
  rootRef?: RefObject<HTMLDivElement>
) {
  const nodeBounds: Map<K1, Bounds> = new Map();
  const nodeAbsBounds: Map<K1, Bounds> = new Map();
  dimensions.forEach((value, key) => {
    const point = nodePoints.get(key);
    const absPoint = getAbsoluteNodePosition(key, undefined, rootRef);
    if (point) {
      nodeBounds.set(key, { x: point.x, y: point.y, width: value.width, height: value.height });
    }
    if (absPoint) {
      nodeAbsBounds.set(key, { x: absPoint.x, y: absPoint.y, width: value.width, height: value.height });
    }
  });
  return { nodeBounds, nodeAbsBounds };
}

/**
 * Deep equality check for whether two arrays of bounds are the same
 */
export function isEqualBounds<K1>(bounds1: Map<K1, Bounds>, bounds2: Map<K1, Bounds>) {
  if (bounds1.size !== bounds2.size) return false;
  let equal = true;
  bounds1.forEach((value, key) => {
    const b2Value = bounds2.get(key);
    if (!b2Value) equal = false;
    else if (
      Math.abs(value.x - b2Value.x) > 0.5 ||
      Math.abs(value.y - b2Value.y) > 0.5 ||
      Math.abs(value.width - b2Value.width) > 0.5 ||
      Math.abs(value.height - b2Value.height) > 0.5
    )
      equal = false;
  });
  return equal;
}

/**
 * Returns the text for the diagram datatip.
 */
export function getDatatipContent<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  nodesMap: Map<K1, D1>,
  linksMap: Map<K2, D2>,
  focusedItem?: ItemInfo<K1, K2, D2>,
  hoveredItem?: ItemInfo<K1, K2, D2>,
  datatip?: DatatipConfig<DatatipContext<K1, D1, D2>>
) {
  let itemInfo;
  const isPointerActive = hoveredItem?.isCurrent;
  if (isPointerActive && hoveredItem.id != null) {
    itemInfo = hoveredItem;
  }
  if (focusedItem && focusedItem.isCurrent) {
    itemInfo = focusedItem;
  }
  if (itemInfo && datatip) {
    let context: DatatipContext<K1, D1, D2>;
    switch (itemInfo.type) {
      case 'node': {
        context = { data: nodesMap.get(itemInfo.id as K1) as D1, type: 'node' };
        break;
      }
      case 'link': {
        context = { data: linksMap.get(itemInfo.id as K2) as D2, type: 'link' };
        break;
      }
      default: {
        context = { id: itemInfo.id as PromotedLinkId<K1, D2>, type: 'promotedLink' };
      }
    }

    return datatip(context);
  }
  if (itemInfo) {
    const text = (
      itemInfo.type === 'node' ? nodesMap.get(itemInfo.id as K1) : linksMap.get(itemInfo.id as K2)
    )?.accessibleLabel;
    return {
      content: text
    };
  }
  return;
}

/**
 * Creates a map with key id and value node or link data
 */
export function createLinksMap<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  items: (D2 | DiagramLinkData<string, K1>)[]
) {
  const itemIdToDataMap = new Map();
  const itemIdToIndexMap = new Map();
  const itemIndexToIdMap = new Map();
  const itemIdToParentMap = new Map();
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    itemIdToDataMap.set(item.id, item);
    itemIdToIndexMap.set(item.id, i);
    itemIndexToIdMap.set(i, item.id);
  }
  return {
    itemIdToDataMap,
    itemIdToIndexMap,
    itemIndexToIdMap,
    itemIdToParentMap
  };
}

/**
 * Creates a map with key id and value node or link data
 */
export function createPromotedLinksMap<K1, D2>(items: PromotedLinkId<K1, D2>[]) {
  const promotedLinkMap = new Map();
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    promotedLinkMap.set(`${item.startNode}_${item.endNode}`, item);
  }
  return promotedLinkMap;
}

/**
 * Creates a map with key id and value node or link data
 */
export function createNodesMap<K1, D1 extends DiagramNodeData<K1, D1>>(
  items: D1[],
  expanded: Keys<K1>
) {
  const itemIdToDataMap = new Map();
  const itemIdToIndexMap = new Map();
  const itemIndexToIdMap = new Map();
  const itemIdToParentMap = new Map();
  const allContainerNodes = [];
  const allVisibleNodes = [];
  const allVisibleNodesData = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const index = String(i);
    itemIdToDataMap.set(item.id, item);
    itemIdToIndexMap.set(item.id, index);
    itemIndexToIdMap.set(index, item.id);
    allVisibleNodes.push(item.id);
    allVisibleNodesData.push(item);
    if ((item as D1).nodes) {
      allContainerNodes.push(item);
      if (containsKey(expanded, item.id)) {
        _createItemMap<K1, D1>(
          (item as D1).nodes!,
          itemIdToDataMap,
          itemIdToIndexMap,
          itemIndexToIdMap,
          itemIdToParentMap,
          allContainerNodes,
          expanded,
          allVisibleNodes,
          item.id as K1,
          String(i)
        );
      }
    }
  }
  return {
    itemIdToDataMap,
    itemIdToIndexMap,
    itemIndexToIdMap,
    itemIdToParentMap,
    allContainerNodes,
    allVisibleNodes
  };
}

function _createItemMap<K1, D1 extends DiagramNodeData<K1, D1>>(
  items: D1[],
  itemIdToDataMap: Map<K1, D1>,
  itemIdToIndexMap: Map<K1, string>,
  itemIndexToIdMap: Map<string, K1>,
  itemIdToParentMap: Map<K1, K1>,
  allContainerNodes: D1[],
  expanded: Keys<K1>,
  allVisibleNodes: K1[],
  parentId: K1,
  parentIndex: string
) {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const index = `${parentIndex}_${i}`;
    itemIdToDataMap.set(item.id, item);
    itemIdToIndexMap.set(item.id, index);
    itemIndexToIdMap.set(index, item.id);
    itemIdToParentMap.set(item.id, parentId);
    allVisibleNodes.push(item.id);
    if (item.nodes) {
      allContainerNodes.push(item);
      if (containsKey(expanded, item.id)) {
        _createItemMap(
          item.nodes,
          itemIdToDataMap,
          itemIdToIndexMap,
          itemIndexToIdMap,
          itemIdToParentMap,
          allContainerNodes,
          expanded,
          allVisibleNodes,
          item.id,
          index
        );
      }
    }
  }
}

/**
 * Filter out links with no start or end nodes
 */
export function filterLinksWithNoStartOrEndNodes<K2, K1, D1, D2 extends DiagramLinkData<K2, K1>>(
  links: D2[],
  nodesMap: Map<K1, D1>
) {
  const newLinks: D2[] = [];
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    if (nodesMap.get(link.startNode) && nodesMap.get(link.endNode)) {
      newLinks.push(link);
    }
  }
  return newLinks;
}

/**
 * Pulls out the dimensions from an array of bounds
 */
export function getDimensionsFromBounds<K1>(bounds: Map<K1, Bounds>) {
  const dims: Map<K1, Dimensions> = new Map();
  bounds.forEach((value, key) => {
    dims.set(key, { width: value.width, height: value.height });
  });
  return dims;
}

/**
 * Returns the center position of the node or link
 * For links, draws a straight line between the centers of the start/end node
 * and returns the center of that line
 */
export function getFocusedItemBounds<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  focusedItemInfo: ItemInfo<K1, K2, D2>,
  bounds: Map<K1, Bounds>,
  links: D2[],
  linkIdToIndexMap: Map<K2, number>,
  rootRef?: RefObject<HTMLDivElement>
) {
  if (focusedItemInfo.type === 'node') {
    // Return the first element if cannot find the Node index
    const position = getAbsoluteNodePosition(focusedItemInfo.id as K1, undefined, rootRef);
    const nodeBounds = bounds.get(focusedItemInfo.id as K1) || bounds.values().next().value!;
    return {
      x: position ? position.x : nodeBounds.x,
      y: position ? position.y : nodeBounds.y,
      width: nodeBounds.width,
      height: nodeBounds.height
    };
  } else {
    // Center of a straight line between start/end node centers
    const link =
      focusedItemInfo.type === 'promotedLink'
        ? focusedItemInfo.id
        : links[linkIdToIndexMap.get(focusedItemInfo.id as K2) || 0];
    const startNB = bounds.get(link.startNode) || bounds.values().next().value!;
    const endNB = bounds.get(link.endNode) || bounds.values().next().value!;
    const startNCenter = {
      x: startNB.x + startNB.width / 2,
      y: startNB.y + startNB.height / 2
    };
    const endNCenter = { x: endNB.x + endNB.width / 2, y: endNB.y + endNB.height / 2 };

    const minX = Math.min(startNCenter.x, endNCenter.x);
    const minY = Math.min(startNCenter.y, endNCenter.y);
    return {
      x: minX,
      y: minY,
      width: Math.abs(startNCenter.x - endNCenter.x),
      height: Math.abs(startNCenter.y - endNCenter.y)
    };
  }
}

/**
 * Returns the bounds of the diagram content
 */
export function getContentBounds<K1>(nodeBounds: Map<K1, Bounds>, labelBounds?: Bounds): Bounds {
  if (nodeBounds.size === 0) return { x: 0, width: Infinity, y: 0, height: Infinity };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let dims;
  nodeBounds.forEach((value) => {
    dims = value;
    minX = dims.x < minX ? dims.x : minX;
    minY = dims.y < minY ? dims.y : minY;
    maxX = dims.x + dims.width > maxX ? dims.x + dims.width : maxX;
    maxY = dims.y + dims.height > maxY ? dims.y + dims.height : maxY;
  });
  if (labelBounds) {
    minX = labelBounds.x < minX ? labelBounds.x : minX;
    minY = labelBounds.y < minY ? labelBounds.y : minY;
    maxX = labelBounds.x + labelBounds.width > maxX ? labelBounds.x + labelBounds.width : maxX;
    maxY = labelBounds.y + labelBounds.height > maxY ? labelBounds.y + labelBounds.height : maxY;
  }
  return { x: minX, width: maxX - minX, y: minY, height: maxY - minY };
}

/**
 * Returns the label bounds
 */
export function getAllLabelBounds<K1, K2, D1 extends DiagramNodeData<K1, D1>>(
  labelDims: Map<K1 | K2, TextDimensions>,
  nodeLabelPos: Map<K1, DiagramLabelPosition> = new Map(),
  linkLabelPos: Map<K2, DiagramLabelPosition> = new Map(),
  isRTL: boolean,
  rootRef?: RefObject<HTMLDivElement>
) {
  if (nodeLabelPos.size === 0) return undefined;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let dims;
  let nodeBound;

  nodeLabelPos.forEach((value, key) => {
    dims = getLabelBounds(value, labelDims.get(key)!, isRTL);
    nodeBound = getAbsoluteNodePosition(key, null, rootRef);
    if (nodeBound) {
      minX = dims.x + nodeBound.x < minX ? dims.x + nodeBound.x : minX;
      minY = dims.y + nodeBound.y < minY ? dims.y + nodeBound.y : minY;
      maxX = dims.x + dims.width + nodeBound.x > maxX ? dims.x + dims.width + nodeBound.x : maxX;
      maxY = dims.y + dims.height + nodeBound.y > maxY ? dims.y + dims.height + nodeBound.y : maxY;
    }
  });
  linkLabelPos.forEach((value, key) => {
    dims = getLabelBounds(value, labelDims.get(key)!, isRTL);
    if (dims) {
      minX = dims.x < minX ? dims.x : minX;
      minY = dims.y < minY ? dims.y : minY;
      maxX = dims.x + dims.width > maxX ? dims.x + dims.width : maxX;
      maxY = dims.y + dims.height > maxY ? dims.y + dims.height : maxY;
    }
  });
  return { x: minX, width: maxX - minX, y: minY, height: maxY - minY };
}

function getLabelBounds(label: DiagramLabelPosition, dims: TextDimensions, isRTL: boolean) {
  const labelPos = getLabelPosition(dims, label, isRTL);
  const isRotating = label.rotationAngle;
  if (!isRotating) {
    return { x: labelPos.x, y: labelPos.y, width: dims.width, height: dims.height };
  } else {
    // Find the 4 corners of the label bounds relative to the rotation point
    const rotationPoint = label.rotationPoint
      ? label.rotationPoint
      : { x: dims.width / 2, y: dims.height / 2 };
    const angle = label.rotationAngle!;
    const topLeft = getCoordsRelToRotPoint({ x: 0, y: 0 }, rotationPoint);
    const topRight = getCoordsRelToRotPoint({ x: dims.width, y: 0 }, rotationPoint);
    const bottomLeft = getCoordsRelToRotPoint({ x: 0, y: dims.height }, rotationPoint);
    const bottomRight = getCoordsRelToRotPoint({ x: dims.width, y: dims.height }, rotationPoint);
    const rotTopLeft = rotatePoint(topLeft, angle);
    const rotBotLeft = rotatePoint(bottomLeft, angle);
    const rotTopRight = rotatePoint(topRight, angle);
    const rotBotRight = rotatePoint(bottomRight, angle);
    const points = [rotTopLeft, rotBotLeft, rotTopRight, rotBotRight].map((pos: Position) => {
      return {
        x: pos.x + labelPos.x + rotationPoint.x,
        y: pos.y + labelPos.y + rotationPoint.y
      };
    });
    return getBoundsFromPoints(points);
  }
}

function getCoordsRelToRotPoint(labelPoint: Position, rotPoint: Position) {
  return { x: labelPoint.x - rotPoint.x, y: labelPoint.y - rotPoint.y };
}

function rotatePoint(pos: Position, angle: number) {
  return {
    x: pos.x * Math.cos(angle) - pos.y * Math.sin(angle),
    y: pos.x * Math.sin(angle) + pos.y * Math.cos(angle)
  };
}

function getBoundsFromPoints(points: Position[]) {
  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (point.x < minX) minX = point.x;
    else if (point.x > maxX) maxX = point.x;
    if (point.y < minY) minY = point.y;
    else if (point.y > maxY) maxY = point.y;
  }
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Returns the position of the label
 * @param dimensions
 * @param positionProps
 * @returns position of the label
 */
export function getLabelPosition(
  dimensions: TextDimensions,
  positionProps: DiagramLabelPosition,
  isRTL: boolean
) {
  const position = positionProps.position;
  const hAlign = positionProps.hAlign ? positionProps.hAlign : isRTL ? 'right' : 'left';
  const vAlign = positionProps.vAlign;
  let x, y;
  if (hAlign === 'center') {
    x = position.x - dimensions.width / 2;
  } else if (hAlign === 'right') {
    x = position.x - dimensions.width;
  } else {
    x = position.x;
  }

  if (vAlign === 'middle') {
    y = position.y - dimensions.height / 2;
  } else if (vAlign === 'bottom') {
    y = position.y - dimensions.height;
  } else if (vAlign === 'baseline') {
    y = position.y + dimensions.y;
  } else {
    y = position.y;
  }
  return {
    x: x,
    y: y
  };
}

/**
 * Compares if two ItemInfo are equal
 */
export function isEqualItem<K1, K2, D2>(
  item1?: ItemInfo<K1, K2, D2>,
  item2?: ItemInfo<K1, K2, D2>
) {
  return item1?.id === item2?.id && item1?.type === item2?.type;
}

/**
 * Returns the item info of the data-oj-node or data-oj-link attribute for a given element.
 */
export function getNavigableInfo<K1, K2, D2>(
  element: HTMLElement,
  promotedLinkMap: Map<string, PromotedLinkId<K1, D2>>,
  rootElementRef?: RefObject<HTMLDivElement>
): ItemInfo<K1, K2, D2> {
  let type;
  let elem = element;
  let navigableId;
  while (!navigableId) {
    if (elem.dataset['ojPrivateNode']) {
      type = 'node';
      navigableId = elem.dataset['ojPrivateNode'];
    } else if (elem.dataset['ojPrivateLink']) {
      navigableId = elem.dataset['ojPrivateLink'];
      type = 'link';
    } else if (elem.dataset['ojPrivatePromotedLink']) {
      navigableId = promotedLinkMap.get(elem.dataset['ojPrivatePromotedLink']);
      type = 'promotedLink';
    }
    if (!navigableId) {
      if (elem.parentElement && elem.parentElement !== rootElementRef?.current) {
        elem = elem.parentElement;
      } else {
        break;
      }
    }
  }
  return {
    id: navigableId as K1 | K2,
    type: type
  } as ItemInfo<K1, K2, D2>;
}

/**
 * Returns the id of the node the expand collapse button belongs to
 */
export function expCollapseButtonId<K1>(
  element: HTMLElement,
  rootElementRef?: RefObject<HTMLDivElement>
): K1 | undefined {
  let elem = element;
  let id;
  while (!id) {
    id = elem.dataset['ojPrivateButton'] as K1;
    if (!id) {
      if (
        elem.parentElement &&
        !elem.parentElement.dataset['ojPrivateNode'] &&
        elem.parentElement !== rootElementRef?.current
      ) {
        elem = elem.parentElement;
      } else {
        break;
      }
    }
  }
  return id;
}

/**
 * Returns the aria label
 */
export function getItemAriaLabel(
  translations: BundleType,
  supportsSelection: boolean,
  states: {
    isSelected?: boolean,
    isIndirect?: boolean,
    isExpanded?: boolean,
    isCollapsed?: boolean
  },
  accessibleLabel?: string,
) {
  return generateAriaLabel(translations, accessibleLabel || '', {
    isSelected: !supportsSelection ? undefined : states.isSelected,
    isIndirect: states.isIndirect,
    isExpanded: states.isExpanded,
    isCollapsed: states.isCollapsed
  });
}

/**
 * Returns the font props for a node or link
 */
function _getFontProps(styles?: DiagramLabelStyles) {
  const fontProps: Font = {};
  if (!styles) return fontProps;
  if (styles.fontFamily) fontProps.fontFamily = styles.fontFamily;
  if (styles.fontSize) fontProps.fontSize = styles.fontSize;
  if (styles.fontStyle) fontProps.fontStyle = styles.fontStyle;
  if (styles.fontWeight) fontProps.fontWeight = styles.fontWeight;
  return fontProps;
}

/**
 * Returns whether the node is a leaf node
 */

export function isLeafNode<K1, D1 extends DiagramNodeData<K1, D1>>(nodeData: D1) {
  return !nodeData.nodes;
}

export function isContainerReady<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  nodeData: D1,
  nodeBounds: Map<K1, Bounds>,
  links: D2[],
  promotedLinks: PromotedLinkId<K1, D2>[],
  linkDimensions: Map<K2, Dimensions>,
  promotedLinkDimensions: Map<string, Dimensions>
) {
  const nodes = nodeData.nodes!;
  for (let i = 0; i < nodes?.length; i++) {
    const child = nodes[i].id;
    if (!nodeBounds.get(child)) {
      return false;
    }
  }
  for (let i = 0; i < links.length; i++) {
    const link = links[i].id;
    const linkDims = linkDimensions.get(link);
    if (!linkDims || (linkDims.width === 0 && linkDims.height === 0)) {
      return false;
    }
  }
  for (let i = 0; i < promotedLinks.length; i++) {
    const link = promotedLinks[i];
    const linkDims = promotedLinkDimensions.get(getPromotedString(link));
    if (!linkDims || (linkDims.width === 0 && linkDims.height === 0)) {
      return false;
    }
  }
  return true;
}

export function getPromotedString<K1, D2>(id: PromotedLinkId<K1, D2>) {
  return `${id.startNode}_${id.endNode}`;
}

export function comparePromotedLinks<K1, D2>(
  link1: PromotedLinkId<K1, D2>,
  link2: PromotedLinkId<K1, D2>
) {
  return link1.startNode === link2.startNode && link1.endNode === link2.endNode;
}

export function isPromoted<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  link: D2 | PromotedLinkId<K1, D2>
) {
  return !('id' in link);
}

function getCommonAncestorId<K1>(startId: K1, endId: K1, nodeIdToParentMap: Map<K1, K1>) {
  const startAnc = _getAncestorIds(startId, nodeIdToParentMap);
  const endAnc = _getAncestorIds(endId, nodeIdToParentMap);
  for (let i = 0; i < startAnc.length; i++) {
    if (endAnc.indexOf(startAnc[i]) > -1) {
      return startAnc[i];
    }
  }
  return undefined;
}

export function getLinksForNode<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  containerId: K1 | undefined,
  links: D2[],
  promotedLinks: PromotedLinkId<K1, D2>[],
  nodeIdToParentMap: Map<K1, K1>
) {
  // front links are in front of nodes
  const frontLinks = [];
  const backLinks = [];
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    const ancId = getCommonAncestorId(link.startNode, link.endNode, nodeIdToParentMap);
    if (ancId === containerId) {
      if (!nodeIdToParentMap.get(link.startNode) && !nodeIdToParentMap.get(link.endNode))
        backLinks.push(link);
      else frontLinks.push(link);
    }
  }
  const frontPromotedLinks = [];
  const backPromotedLinks = [];
  for (let i = 0; i < promotedLinks.length; i++) {
    const link = promotedLinks[i];
    const ancId = getCommonAncestorId(link.startNode, link.endNode, nodeIdToParentMap);
    if (ancId === containerId) {
      if (!nodeIdToParentMap.get(link.startNode) && !nodeIdToParentMap.get(link.endNode))
        backPromotedLinks.push(link);
      else frontPromotedLinks.push(link);
    }
  }
  return { frontLinks, frontPromotedLinks, backLinks, backPromotedLinks };
}

function _getAncestorIds<K1, D1 extends DiagramNodeData<K1, D1>>(nodeId: K1, nodes: Map<K1, K1>) {
  const ancestors = [];
  let anc = nodes.get(nodeId);
  while (anc !== undefined) {
    ancestors.push(anc);
    anc = nodes.get(anc);
  }
  return ancestors;
}

export function containerLinksReady<
  K1 extends string | number,
  K2 extends string | number,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
>(containerLinks: (D2 | PromotedLinkId<K1, D2>)[], layoutOutput: L) {
  const nodes = layoutOutput.nodes;
  for (let i = 0; i < containerLinks.length; i++) {
    const link = containerLinks[i];
    if (!(nodes[link.startNode] && nodes[link.endNode])) {
      return false;
    }
  }
  return true;
}

export function changeExpanded<K1>(id: K1, expanded: Keys<K1>) {
  if (containsKey(expanded, id)) {
    return removeKey(expanded, id, false);
  } else {
    return addKey(expanded, id);
  }
}

export function getPromotedLinks<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(links: D2[], expanded: Keys<K1>, allContainerNodes: D1[], allVisibleNodes: K1[]) {
  const collapsed: D1[] = [];
  allContainerNodes.forEach((node: D1) => {
    if (!containsKey(expanded, node.id)) {
      collapsed.push(node);
    }
  });
  const promotedLinks = [];
  const linkToPromotedMap = new Map<K2, string>();
  const promotedToLinkMap = new Map<string, D2[]>();
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    const linkId = link.id;
    const startVisible = allVisibleNodes.includes(link.startNode)
      ? link.startNode
      : firstVisibleAncestor(link.startNode, collapsed);
    const endVisible = allVisibleNodes.includes(link.endNode)
      ? link.endNode
      : firstVisibleAncestor(link.endNode, collapsed);
    if (
      startVisible &&
      endVisible &&
      (startVisible !== link.startNode || endVisible !== link.endNode) &&
      startVisible !== endVisible
    ) {
      const promotedLinkId = `${startVisible}_${endVisible}`;
      let subLinks = promotedToLinkMap.get(promotedLinkId);
      if (subLinks) {
        subLinks.push(link);
      } else {
        const promotedLink = {
          id: promotedLinkId,
          startNode: startVisible,
          endNode: endVisible
        };
        promotedLinks.push(promotedLink);
        subLinks = [link];
      }
      promotedToLinkMap.set(promotedLinkId, subLinks);
      linkToPromotedMap.set(linkId, promotedLinkId);
    }
  }
  const finalPromoted = [];
  for (let i = 0; i < promotedLinks.length; i++) {
    const promotedLink = promotedLinks[i];
    finalPromoted.push({
      startNode: promotedLink.startNode,
      endNode: promotedLink.endNode,
      links: promotedToLinkMap.get(promotedLink.id)!
    });
  }
  return finalPromoted;
}

// One of the start/end node position changed causing the link position to change
export function linkPositionChanged<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  nodeBounds: Map<K1, Bounds>,
  bounds: Map<K1, Bounds>,
  links: D2[]
) {
  const nodesWithLinks: K1[] = [];
  links.forEach((value) => {
    if (!nodesWithLinks.includes(value.startNode)) {
      nodesWithLinks.push(value.startNode);
    }
    if (!nodesWithLinks.includes(value.endNode)) {
      nodesWithLinks.push(value.endNode);
    }
  });
  let rerender = false;
  nodeBounds.forEach((value, key) => {
    const b2Value = bounds.get(key);
    let equal = true;
    if (!b2Value) equal = false;
    else if (
      Math.abs(value.x - b2Value.x) > 0.5 ||
      Math.abs(value.y - b2Value.y) > 0.5 ||
      Math.abs(value.width - b2Value.width) > 0.5 ||
      Math.abs(value.height - b2Value.height) > 0.5
    ) {
      equal = false;
    }
    if (!equal && nodesWithLinks.includes(key)) {
      rerender = true;
    }
  });
  return rerender;
}

export function getChildInfo<K1, D1 extends DiagramNodeData<K1, D1>>(
  id: K1,
  nodeIdToDataMap: Map<K1, D1>
) {
  const nodes = nodeIdToDataMap.get(id)?.nodes;
  if (nodes && nodes.length > 0) {
    return nodes[0];
  }
  return undefined;
}

function firstVisibleAncestor<K1, D1 extends DiagramNodeData<K1, D1>>(nodeId: K1, collapsed: D1[]) {
  const isDescendant = (chId: K1, data?: D1[]) => {
    if (data) {
      for (let i = 0; i < data.length; i++) {
        if (data[i].id === chId || isDescendant(chId, data[i].nodes)) {
          return true;
        }
      }
    }
    return false;
  };
  for (let i = 0; i < collapsed.length; i++) {
    const node = collapsed[i];
    if (isDescendant(nodeId, node.nodes)) {
      return node.id;
    }
  }
  return null;
}
